import React, { useEffect, useMemo, useState } from 'react';
import BulkEditRequestsSelector from './Partials/BulkEditRequestsSelector';
import BulkEditTypeSelector from './Partials/BulkEditTypeSelector';
import { Form, Formik } from 'formik';
import BulkEditorHotels from './Partials/BulkEditorHotels';
import BulkEditorExperiences from './Partials/BulkEditorExperiences';
import BulkEditorTemplates from './Partials/BulkEditorTemplates';
import BulkEditorBottomBar from './Partials/BulkEditorBottomBar';
import { useSnack } from '../../../hooks';
import { useTranslation } from 'react-i18next';
import { parseBoolean } from 'utils';
import KoobLoadingOverlay from '@koob/ui/src/components/KoobLoadingOverlay';
import { updateManyToConnectionRequest } from '../../../api/node/toConnectionRequest';
import { getHotelsContracts } from '../../../api/node/contracts';
import { getRoomsDetail } from '../../../utils/hotelDistribution';
import {
  buildHotelPeriodsPayload,
  buildHotelRoomsPayload,
  completeModelRoomsWithMissing,
  formatModelToFormikData,
  parseMarginValue,
} from '../utils';
import { useKoobStore } from '../../../store';
import { useShallow } from 'zustand/react/shallow';
import { useDistributionGuard } from '../useDistributionGuard';
import DistributionIncoherenceModal from '../Partials/DistributionIncoherenceModal';

export default function ToConnectionRequestBulkEdit() {
  const { t } = useTranslation();
  const { notify } = useSnack();

  const {
    toConnectionRequestModel,
    selectedToConnectionRequestIds,
    type,
    setType,
    clearToConnectionRequestModel,
    clearSelectedToConnectionRequestIds,
  } = useKoobStore(
    useShallow((state) => ({
      toConnectionRequestModel: state.toConnectionRequestModel,
      selectedToConnectionRequestIds: state.selectedToConnectionRequestIds,
      type: state.bulkEditType,
      setType: state.setBulkEditType,
      clearToConnectionRequestModel: state.clearToConnectionRequestModel,
      clearSelectedToConnectionRequestIds:
        state.clearSelectedToConnectionRequestIds,
    })),
  );

  useEffect(() => {
    return () => {
      clearToConnectionRequestModel();
      clearSelectedToConnectionRequestIds();
    };
  }, []);

  const modelFormikData = useMemo(
    () => formatModelToFormikData({ model: toConnectionRequestModel }),
    [toConnectionRequestModel],
  );

  const editorComponents = {
    hotels: <BulkEditorHotels />,
    experiences: <BulkEditorExperiences />,
    templates: <BulkEditorTemplates />,
  };

  const [updatingIds, setUpdatingIds] = useState([]);
  const distributionGuard = useDistributionGuard();
  // The confirmed replay runs outside Formik's submit cycle, so `isSubmitting`
  // is already back to false by then: without this the bulk update would fire
  // with the whole page idle and clickable, and a second click would send it
  // again.
  const [confirmingDistribution, setConfirmingDistribution] = useState(false);

  const confirmDistribution = async () => {
    setConfirmingDistribution(true);
    try {
      await distributionGuard.confirm();
    } finally {
      setConfirmingDistribution(false);
    }
  };

  const submitForm = async (values, { setFieldValue }) => {
    const isApplyingModel = Boolean(values.model || toConnectionRequestModel);

    const hotelsPayload = await Promise.all(
      (values.request.toConnectionRequestHotels ?? []).map(async (hotel) => {
        const isSelected = parseBoolean(hotel.isSelected) === true;
        const isDistribute =
          isSelected && parseBoolean(hotel.distribute) !== false;
        let hotelToSubmit = hotel;

        // KD-3712: when applying a model, any room of the hotel not configured
        // in the model must become invisible on the TOs. Fetch contracts for
        // distributed hotels and append missing rooms as disabled (enabled: 'false').
        // This ensures the fix is complete even if the user never opened the room modal.
        if (isApplyingModel && isDistribute) {
          try {
            const contractsRes = await getHotelsContracts({
              hotelId: hotel.id,
            });
            const currentRooms =
              getRoomsDetail(contractsRes, hotel.periods ?? []) ?? [];
            if (currentRooms.length > 0) {
              const completedRooms = completeModelRoomsWithMissing(
                hotel.rooms ?? [],
                currentRooms,
              );
              hotelToSubmit = {
                ...hotel,
                rooms: completedRooms,
              };
            }
          } catch (error) {
            // Keep original hotel if contract fetching fails
          }
        }

        return {
          id: `${hotelToSubmit.id}`,
          margin: parseMarginValue(hotelToSubmit.margin),
          isMarginPercent: parseBoolean(hotelToSubmit.isMarginPercent),
          distribute: isSelected
            ? parseBoolean(hotelToSubmit.distribute)
            : false,
          contracts: hotelToSubmit.contracts?.map((contract) => ({
            id: `${contract.id}`,
            enabled: parseBoolean(contract.enabled),
          })),
          periods: buildHotelPeriodsPayload(hotelToSubmit),
          rooms: buildHotelRoomsPayload(hotelToSubmit),
        };
      }),
    );

    const request = {
      state: values.request.state ?? undefined,
      margin: parseMarginValue(values.request.margin),
      isMarginPercent: parseBoolean(values.request.isMarginPercent),
      toConnectionRequestHotels: hotelsPayload,
      toConnectionRequestExperiences:
        values.request.toConnectionRequestExperiences?.map((experience) => ({
          id: `${experience.id}`,
          margin: parseMarginValue(experience.margin),
          isMarginPercent: parseBoolean(experience.isMarginPercent),
          distribute:
            parseBoolean(experience.isSelected) === true
              ? parseBoolean(experience.distribute)
              : false,
        })),
      toConnectionRequestTemplates:
        values.request.toConnectionRequestTemplates?.map((template) => ({
          id: `${template.id}`,
          margin: parseMarginValue(template.margin),
          isMarginPercent: parseBoolean(template.isMarginPercent),
          distribute:
            parseBoolean(template.isSelected) === true
              ? parseBoolean(template.distribute)
              : false,
        })),
    };

    const submit = (payload) =>
      updateManyToConnectionRequest({
        toConnectionRequestIds: values.toConnectionRequestIds,
        request: payload,
      })
        .then(() => {
          notify(t('misc:saveSuccessful'));
          setUpdatingIds((current) => [
            ...current,
            ...values.toConnectionRequestIds,
          ]);
          setFieldValue('toConnectionRequestIds', []);
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            notify(err?.response?.data?.message, { type: 'error' });
          } else {
            notify(t('misc:saveError'), { type: 'error' });
          }
        });

    // A bulk can span many TOs at once, so the incoherence list is where the
    // DMC discovers which of them would receive an unusable template.
    await distributionGuard.guard({
      toConnectionRequestIds: values.toConnectionRequestIds,
      request,
      submit,
    });
  };

  const initialValues = useMemo(() => {
    if (toConnectionRequestModel) {
      return {
        toConnectionRequestIds: selectedToConnectionRequestIds,
        request: {
          state: null,
          margin: null,
          isMarginPercent: null,
          ...modelFormikData,
        },
        model: {
          id: toConnectionRequestModel.id,
          name: toConnectionRequestModel.name,
          description: toConnectionRequestModel.description,
        },
      };
    }

    return {
      toConnectionRequestIds: selectedToConnectionRequestIds,
      request: {
        state: null,
        margin: null,
        isMarginPercent: null,
        toConnectionRequestHotels: [],
        toConnectionRequestExperiences: [],
        toConnectionRequestTemplates: [],
      },
      model: null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toConnectionRequestModel, modelFormikData]);

  return (
    <div className="max-w-7xl mx-auto min-h-[150vh] px-4 py-8">
      <>
        <Formik
          initialValues={initialValues}
          onSubmit={submitForm}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form>
              {(isSubmitting || confirmingDistribution) && (
                <KoobLoadingOverlay
                  opacity="semi"
                  title={t('toConnectionRequest:saving.title')}
                  description={t('toConnectionRequest:saving.description')}
                />
              )}

              <div className="flex-col space-y-5">
                <BulkEditRequestsSelector
                  updatingIds={updatingIds}
                  setUpdatingIds={setUpdatingIds}
                />

                {/* <BulkEditGlobalMargin /> */}

                <BulkEditTypeSelector type={type} setType={setType}>
                  {editorComponents[type]}
                </BulkEditTypeSelector>

                <BulkEditorBottomBar loading={isSubmitting} />
              </div>
            </Form>
          )}
        </Formik>

        <DistributionIncoherenceModal
          open={distributionGuard.isOpen}
          incoherences={distributionGuard.incoherences}
          isBulk
          submitting={confirmingDistribution}
          onCancel={distributionGuard.cancel}
          onConfirm={confirmDistribution}
        />
      </>
    </div>
  );
}
